/**
 * Template Library
 *
 * Modal dialog for browsing and applying schedule templates.
 */

import { useState, useMemo, useCallback } from 'react';
import type { ScheduleTemplate, ScheduleCategory } from './types';
import { scheduleTemplates, searchTemplates } from './templates';

/**
 * Category display names
 */
const CATEGORY_NAMES: Record<ScheduleCategory, string> = {
  occupancy: 'Occupancy',
  lighting: 'Lighting',
  equipment: 'Equipment',
  heating: 'Heating',
  cooling: 'Cooling',
  thermostat: 'Thermostat',
  hvac: 'HVAC',
  ventilation: 'Ventilation',
  activity: 'Activity',
  availability: 'Availability',
  other: 'Other',
};

/**
 * Building types
 */
const BUILDING_TYPES = [
  { id: 'all', name: 'All Buildings' },
  { id: 'office', name: 'Office' },
  { id: 'residential', name: 'Residential' },
  { id: 'school', name: 'School' },
  { id: 'retail', name: 'Retail' },
  { id: 'hospital', name: 'Hospital' },
  { id: 'restaurant', name: 'Restaurant' },
  { id: 'warehouse', name: 'Warehouse' },
];

/**
 * Mini bar preview for template
 */
function TemplateThumbnail({ template }: { template: ScheduleTemplate }) {
  // Generate simplified preview bars for Monday
  const bars = useMemo(() => {
    const dayValues = template.weekPattern.monday;
    const result: number[] = new Array(24).fill(0);

    // Parse time values
    let currentValue = 0;
    for (const tv of dayValues) {
      const hour = tv.hour;
      for (let h = 0; h < 24 && h < hour; h++) {
        if (result[h] === 0) result[h] = currentValue;
      }
      currentValue = tv.value;
    }

    // Normalize to 0-1
    const max = Math.max(...result, 1);
    return result.map((v) => v / max);
  }, [template]);

  return (
    <div className="flex h-8 items-end gap-px bg-muted rounded p-1">
      {bars.map((height, i) => (
        <div
          key={i}
          className="flex-1 bg-primary/70 rounded-t transition-all"
          style={{ height: `${height * 100}%`, minHeight: height > 0 ? '2px' : '0' }}
        />
      ))}
    </div>
  );
}

/**
 * Template card component
 */
function TemplateCard({
  template,
  onSelect,
  onPreview,
}: {
  template: ScheduleTemplate;
  onSelect: () => void;
  onPreview: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 hover:border-primary/50 hover:shadow-sm transition-all">
      {/* Thumbnail preview */}
      <TemplateThumbnail template={template} />

      {/* Template info */}
      <div className="mt-3">
        <h4 className="text-sm font-medium text-foreground">{template.name}</h4>
        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{template.description}</p>
        {template.source && (
          <p className="mt-1 text-xs text-muted-foreground/70 italic">{template.source}</p>
        )}
      </div>

      {/* Actions */}
      <div className="mt-3 flex gap-2">
        <button
          onClick={onSelect}
          className="flex-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Apply
        </button>
        <button
          onClick={onPreview}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        >
          Preview
        </button>
      </div>
    </div>
  );
}

/**
 * Template preview panel
 */
function TemplatePreview({
  template,
  onClose,
  onApply,
}: {
  template: ScheduleTemplate;
  onClose: () => void;
  onApply: () => void;
}) {
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const days = [
    template.weekPattern.monday,
    template.weekPattern.tuesday,
    template.weekPattern.wednesday,
    template.weekPattern.thursday,
    template.weekPattern.friday,
    template.weekPattern.saturday,
    template.weekPattern.sunday,
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded-lg bg-popover shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">{template.name}</h3>
            <p className="text-sm text-muted-foreground">{template.description}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Close preview"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-y-auto p-6">
          {/* Week pattern */}
          <div className="space-y-3">
            {days.map((dayValues, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-24 text-sm font-medium text-muted-foreground">{dayNames[i]}</div>
                <div className="flex-1 flex h-6 items-end gap-px bg-muted rounded">
                  {(() => {
                    const bars: number[] = new Array(24).fill(0);
                    let currentValue = 0;
                    for (const tv of dayValues) {
                      const hour = tv.hour;
                      for (let h = 0; h < 24 && h < hour; h++) {
                        if (bars[h] === 0) bars[h] = currentValue;
                      }
                      currentValue = tv.value;
                    }
                    const max = Math.max(...bars, 1);
                    return bars.map((v, j) => (
                      <div
                        key={j}
                        className="flex-1 bg-primary/70 rounded-t"
                        style={{ height: `${(v / max) * 100}%`, minHeight: v > 0 ? '2px' : '0' }}
                      />
                    ));
                  })()}
                </div>
              </div>
            ))}
          </div>

          {/* Metadata */}
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Type Limits:</span>
              <span className="ml-2 font-medium text-foreground">{template.typeLimits}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Category:</span>
              <span className="ml-2 font-medium text-foreground">{CATEGORY_NAMES[template.category]}</span>
            </div>
            {template.source && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Source:</span>
                <span className="ml-2 font-medium text-foreground">{template.source}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Cancel
          </button>
          <button
            onClick={onApply}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Apply Template
          </button>
        </div>
      </div>
    </div>
  );
}

interface TemplateLibraryProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when a template is selected */
  onSelect: (template: ScheduleTemplate, name: string) => void;
}

/**
 * Template Library Component
 */
export function TemplateLibrary({ isOpen, onClose, onSelect }: TemplateLibraryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ScheduleCategory | null>(null);
  const [selectedBuildingType, setSelectedBuildingType] = useState<string>('all');
  const [previewTemplate, setPreviewTemplate] = useState<ScheduleTemplate | null>(null);
  const [scheduleName, setScheduleName] = useState('');

  // Filter templates
  const filteredTemplates = useMemo(() => {
    let templates = scheduleTemplates;

    // Filter by search query
    if (searchQuery.trim()) {
      templates = searchTemplates(searchQuery);
    }

    // Filter by category
    if (selectedCategory) {
      templates = templates.filter((t) => t.category === selectedCategory);
    }

    // Filter by building type
    if (selectedBuildingType !== 'all') {
      templates = templates.filter(
        (t) => t.buildingTypes.includes(selectedBuildingType) || t.buildingTypes.includes('all')
      );
    }

    return templates;
  }, [searchQuery, selectedCategory, selectedBuildingType]);

  // Group templates by category for display
  const groupedTemplates = useMemo(() => {
    if (selectedCategory) {
      return new Map([[selectedCategory, filteredTemplates]]);
    }

    const grouped = new Map<ScheduleCategory, ScheduleTemplate[]>();
    for (const template of filteredTemplates) {
      const existing = grouped.get(template.category) || [];
      grouped.set(template.category, [...existing, template]);
    }
    return grouped;
  }, [filteredTemplates, selectedCategory]);

  // Handle template selection
  const handleSelect = useCallback(
    (template: ScheduleTemplate) => {
      const name = scheduleName.trim() || template.name;
      onSelect(template, name);
      setScheduleName('');
      onClose();
    },
    [scheduleName, onSelect, onClose]
  );

  const categories: ScheduleCategory[] = [
    'occupancy',
    'lighting',
    'equipment',
    'heating',
    'cooling',
    'hvac',
    'availability',
  ];

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
        <div className="flex h-[80vh] w-full max-w-4xl flex-col rounded-lg bg-popover shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Schedule Template Library</h2>
              <p className="text-sm text-muted-foreground">
                {scheduleTemplates.length} templates available
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Close library"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
              </svg>
            </button>
          </div>

          {/* Filters */}
          <div className="border-b border-border px-6 py-4">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="flex-1 min-w-48">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search templates..."
                    className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </div>
              </div>

              {/* Building type filter */}
              <div>
                <select
                  value={selectedBuildingType}
                  onChange={(e) => setSelectedBuildingType(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {BUILDING_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Schedule name input */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Name:</label>
                <input
                  type="text"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  placeholder="(use template name)"
                  className="w-40 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>

            {/* Category chips */}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
                  selectedCategory === null
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
                    selectedCategory === category
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {CATEGORY_NAMES[category]}
                </button>
              ))}
            </div>
          </div>

          {/* Template grid */}
          <div className="flex-1 overflow-y-auto p-6 bg-muted/30">
            {filteredTemplates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <svg
                  className="h-16 w-16 text-muted-foreground/30"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-4 text-muted-foreground">No templates match your filters</p>
              </div>
            ) : (
              <div className="space-y-8">
                {Array.from(groupedTemplates.entries()).map(([category, templates]) => (
                  <div key={category}>
                    <h3 className="mb-4 text-sm font-semibold text-foreground">
                      {CATEGORY_NAMES[category]}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {templates.map((template) => (
                        <TemplateCard
                          key={template.id}
                          template={template}
                          onSelect={() => handleSelect(template)}
                          onPreview={() => setPreviewTemplate(template)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview modal */}
      {previewTemplate && (
        <TemplatePreview
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onApply={() => {
            handleSelect(previewTemplate);
            setPreviewTemplate(null);
          }}
        />
      )}
    </>
  );
}
